import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { observer } from 'mobx-react-lite'

import { CalendarKyrgyzstan } from 'widgets/kyrgyzstan/calendar-kyrgyzstan'

import { ButtonContinue } from 'shared/ui/bottom-continue'

import { isAvailableDateRange } from '../../lib/is-available-date-range'
import type { IRange } from '../../lib/use-calendar-kyrgyzstan'

import s from './styles.module.scss'

export interface CalendarFormProps {
  initialDates?: IRange
  availableDates?: string[]
  onDatesChange?: (dates: IRange) => void
  onSubmit?: (dates: IRange) => void
  submitButtonText?: string
  showSubmitButton?: boolean
}

const getAvailableInitialDates = (
  initialDates?: IRange,
  availableDates?: string[],
): IRange => {
  if (isAvailableDateRange(initialDates || {}, availableDates)) {
    return initialDates || {}
  }

  const startDate = { startDate: initialDates?.startDate }

  return isAvailableDateRange(startDate, availableDates) ? startDate : {}
}

export const CalendarForm = observer(
  ({
    initialDates,
    availableDates,
    onDatesChange,
    onSubmit,
    submitButtonText,
    showSubmitButton = false,
  }: CalendarFormProps) => {
    const { t } = useTranslation()
    const initialStartDate = initialDates?.startDate
    const initialEndDate = initialDates?.endDate

    const [selectedDates, setSelectedDates] = useState<IRange>(() =>
      getAvailableInitialDates(initialDates, availableDates),
    )

    useEffect(() => {
      if (initialStartDate || initialEndDate) {
        setSelectedDates(
          getAvailableInitialDates(
            { startDate: initialStartDate, endDate: initialEndDate },
            availableDates,
          ),
        )
      }
    }, [initialStartDate, initialEndDate, availableDates])

    const handleDateSelect = (dates: IRange) => {
      setSelectedDates(dates)
      onDatesChange?.(dates)
    }

    const handleSubmit = () => {
      if (
        selectedDates.startDate &&
        selectedDates.endDate &&
        isAvailableDateRange(selectedDates, availableDates)
      ) {
        onSubmit?.(selectedDates)
      }
    }

    const isSubmitDisabled =
      !selectedDates.startDate ||
      !selectedDates.endDate ||
      !isAvailableDateRange(selectedDates, availableDates)

    return (
      <>
        <div className={s.content}>
          <CalendarKyrgyzstan
            selectedDate={selectedDates}
            onDateSelect={handleDateSelect}
            availableDates={availableDates}
          />
        </div>

        {showSubmitButton && (
          <ButtonContinue
            className={s.submitButton}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            <span className="semi-bold-17">
              {submitButtonText || t('kyrgyzstan_block.calendar.select_button')}
            </span>
          </ButtonContinue>
        )}
      </>
    )
  },
)
